/**
 * Reveal — wraps any element to fire a one-shot Tier 1 entrance
 * (Animation Spec §3.3) when it crosses 20% into the viewport.
 *
 * The animation lives in `global.css` (.nl-reveal / .nl-reveal.is-in).
 * This component just attaches the shared IntersectionObserver via `useReveal`.
 */

import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { useReveal } from '@/animation/useReveal';

type AnyTag = keyof JSX.IntrinsicElements;

/**
 * Polymorphic typing: when `as="button"` is passed, the rest of the props
 * are typed against `<button>`'s props (so `type`, `disabled`, etc. flow
 * through without ceremony). Defaults to `<div>` when `as` is omitted.
 */
type RevealProps<T extends AnyTag = 'div'> = {
  as?: T;
  /** Optional so callers can use `dangerouslySetInnerHTML` instead. */
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'ref'>;

export function Reveal<T extends AnyTag = 'div'>({
  as,
  children,
  className,
  ...rest
}: RevealProps<T>): JSX.Element {
  const setRef = useReveal<HTMLElement>();
  const Comp = (as ?? 'div') as ElementType;
  const cls = ['nl-reveal', className].filter(Boolean).join(' ');
  return (
    <Comp ref={setRef} className={cls} {...rest}>
      {children}
    </Comp>
  );
}
